// Connexion : même design que Safecheck-assureur. À gauche, la fenêtre photo (diaporama) ;
// à droite, le formulaire sur un panneau de verre clair.
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, MotionConfig, type Transition } from "framer-motion";
import { AlertCircle, ArrowLeft, ArrowRight, Clock, Eye, EyeOff, Lock, Mail, ShieldCheck } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { loginRequest } from "../api/companyApi";
import { apiRoleToAccountType, getAccountTypeRoute, storeAuth } from "../auth";
import "./Fenetres.css";
import "./LoginPage.css";

const RESSORT: Transition = { type: "spring", stiffness: 170, damping: 26 };
const RESSORT_LOURD: Transition = { type: "spring", stiffness: 120, damping: 24 };
const COURBE = [0.7, 0, 0.2, 1] as const;

const DIAPOS = ["/login1.png", "/login2.png", "/login3.png"] as const;
const DUREE_DIAPO_MS = 5000;

// Entrée en cascade des éléments du formulaire.
const apparition = (rang: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { ...RESSORT, delay: 0.35 + rang * 0.07 },
});

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const resetSuccess =
    location.state && typeof location.state === "object" && "message" in location.state
      ? String((location.state as { message?: string }).message ?? "")
      : null;
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [diapo, setDiapo] = useState(0);

  // Changement d'image automatique ; repart de zéro après un clic sur un point.
  useEffect(() => {
    const minuteur = window.setTimeout(() => setDiapo((i) => (i + 1) % DIAPOS.length), DUREE_DIAPO_MS);
    return () => window.clearTimeout(minuteur);
  }, [diapo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur(null);
    setEnCours(true);

    try {
      const response = await loginRequest({ email: email.trim(), password });
      storeAuth(response.access_token, response.user);
      const accountType = apiRoleToAccountType(response.user.role);
      navigate(getAccountTypeRoute(accountType));
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "Connexion impossible.");
    } finally {
      setEnCours(false);
    }
  };

  return (
    // reducedMotion="user" : animations coupées si le système demande moins de mouvement.
    <MotionConfig reducedMotion="user">
      <div className="sc-login">
        <section className="sc-porte sc-login-visuel">
          <span className="sc-porte-media" aria-hidden="true">
            <motion.span
              className="sc-porte-zoom"
              initial={{ scale: 1.14 }}
              animate={{ scale: [1.14, 1, 1.04] }}
              transition={{ duration: 18, times: [0, 0.1, 1], ease: "easeInOut", repeat: Infinity, repeatType: "mirror" }}
            >
              {/* Diaporama : les photos se relaient en fondu enchaîné. */}
              {DIAPOS.map((src, index) => (
                <motion.img
                  key={src}
                  src={src}
                  alt=""
                  decoding="async"
                  fetchPriority={index === 0 ? "high" : "low"}
                  className="sc-login-diapo"
                  initial={false}
                  animate={{ opacity: index === diapo ? 1 : 0 }}
                  transition={{ duration: 1, ease: "easeInOut" }}
                />
              ))}
            </motion.span>
            <span className="sc-porte-voile" />
            <motion.span className="sc-porte-rideau" initial={{ scaleY: 1 }} animate={{ scaleY: 0 }} transition={{ duration: 0.8, ease: COURBE, delay: 0.05 }} />
          </span>

          <span className="sc-login-points">
            {DIAPOS.map((src, index) => (
              <button
                key={src}
                type="button"
                onClick={() => setDiapo(index)}
                aria-label={`Afficher l'image ${index + 1}`}
                aria-pressed={index === diapo}
                className={index === diapo ? "is-active" : undefined}
              />
            ))}
          </span>

          <span className="sc-porte-corps" aria-hidden="true">
            <motion.span className="sc-porte-surtitre" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ ...RESSORT, delay: 0.55 }}>
              <span className="sc-porte-pastille">
                <ShieldCheck size={18} />
              </span>
              <span className="sc-porte-filet" />
              {t.safecheck.login.platform}
            </motion.span>
            <span className="sc-porte-masque">
              <motion.span className="sc-porte-titre" initial={{ y: "108%" }} animate={{ y: 0 }} transition={{ ...RESSORT_LOURD, delay: 0.45 }}>
                Safecheck
              </motion.span>
            </span>
            <motion.span className="sc-porte-texte" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...RESSORT, delay: 0.7 }}>
              {t.safecheck.login.tagline}
            </motion.span>
            <motion.span className="sc-porte-pied" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.95 }}>
              <motion.span className="sc-porte-ligne" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.9, ease: COURBE, delay: 0.85 }} />
              <span className="sc-porte-mention">{t.safecheck.login.secure}</span>
              <span className="sc-porte-note">Safecheck RDC</span>
            </motion.span>
          </span>
        </section>

        <main id="contenu" className="sc-login-panneau">
          <div className="sc-login-corps">
            <motion.div {...apparition(0)}>
              <a href="https://safecheck-rdc.com" className="sc-login-retour">
                <ArrowLeft size={16} aria-hidden="true" /> {t.safecheck.login.backToHome}
              </a>
            </motion.div>

            <motion.div {...apparition(1)}>
              <img src="/logo-fonce.png" alt="Safecheck RDC" width={254} height={84} fetchPriority="high" className="sc-logo" />
            </motion.div>

            <motion.h1 className="sc-login-titre" {...apparition(2)}>
              {t.safecheck.login.title}
            </motion.h1>
            <motion.p className="sc-login-sous" {...apparition(3)}>
              {t.safecheck.login.subtitle}
            </motion.p>

            {resetSuccess ? <p className="sc-login-succes">{resetSuccess}</p> : null}

            <form onSubmit={handleSubmit}>
              <motion.div {...apparition(4)}>
                <label htmlFor="email" className="sc-login-label">
                  {t.safecheck.login.email}
                </label>
                <div className="sc-login-champ">
                  <Mail size={18} aria-hidden="true" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="username"
                    inputMode="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              </motion.div>

              <motion.div {...apparition(5)}>
                <label htmlFor="password" className="sc-login-label">
                  {t.safecheck.login.password}
                </label>
                <div className="sc-login-champ">
                  <Lock size={18} aria-hidden="true" />
                  <input
                    id="password"
                    name="password"
                    type={visible ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    className="sc-login-oeil"
                    onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    aria-pressed={visible}
                  >
                    {visible ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                  </button>
                </div>
                <Link to="/login/forgot" className="sc-login-oubli">
                  {t.safecheck.login.forgotPassword}
                </Link>
              </motion.div>

              <motion.div {...apparition(6)}>
                <motion.button type="submit" className="sc-login-bouton" disabled={enCours} whileHover={enCours ? undefined : "survol"} whileTap={{ scale: 0.98 }}>
                  {t.safecheck.login.submit}
                  <motion.span className="sc-porte-fleche" aria-hidden="true" variants={{ survol: { x: 4 } }} transition={RESSORT}>
                    {enCours ? <span className="sc-login-spinner" /> : <ArrowRight size={20} />}
                  </motion.span>
                </motion.button>
              </motion.div>

              <div aria-live="polite">
                {erreur && (
                  <motion.p className="sc-login-erreur" role="alert" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={RESSORT}>
                    <AlertCircle size={18} aria-hidden="true" />
                    <span>{erreur}</span>
                  </motion.p>
                )}
              </div>
            </form>

            <motion.p className="sc-login-pied" {...apparition(7)}>
              <Clock size={16} aria-hidden="true" /> {t.safecheck.login.access}
            </motion.p>
          </div>
        </main>
      </div>
    </MotionConfig>
  );
}
